import React from 'react';
import { isPdfPreviewable } from '../pdf/PdfPreviewProvider';
import { SkeletonButton, SkeletonIcon, SkeletonLine, SkeletonPill } from '../Skeleton';
import { formatDateTimeJST } from '../../../utils/datetime';

const MaterialListSkeleton = () => (
  <>
    {Array.from({ length: 4 }, (_, index) => (
      <div key={`eme-skeleton-${index}`} className="eme-row" role="listitem" aria-hidden="true">
        <span className="eme-name">
          <SkeletonIcon size={18} />
          <span className="eme-title">
            <SkeletonLine width={index === 1 ? '64%' : '78%'} height={13} />
            <SkeletonLine width={index === 2 ? '42%' : '56%'} height={10} />
          </span>
        </span>
        <SkeletonPill width={54} height={18} />
        <SkeletonLine width={92} height={10} />
        <span className="eme-actions">
          <SkeletonButton width={32} height={28} />
          <SkeletonButton width={32} height={28} />
        </span>
      </div>
    ))}
  </>
);

export default function EventMaterialExplorerList({
  canManage,
  canDownload,
  canOpenFolderMenu,
  canOpenMaterialMenu,
  clearLongPressTimer,
  clearSpringFolder,
  currentFolderShared,
  downloadMaterial,
  dragOverId,
  emptyLabel,
  folders,
  formatBytes,
  getFileIcon,
  handleExplorerDragOver,
  loadTree,
  loading,
  materials,
  onDropExternal,
  openActionMenu,
  selectedMaterialIds,
  setDragOverId,
  setDragPayload,
  startFolderDrag,
  startLongPressMenu,
  startMaterialDrag,
  suppressLongPressClick,
  toggleMaterialSelected,
  visibleItemsCount,
}) {
  return (
  <div className="eme-list" role="list">
    <div className="eme-list-head" aria-hidden="true">
      <span>名前</span>
      <span>共有</span>
      <span>更新</span>
      <span />
    </div>
    {loading ? (
      <MaterialListSkeleton />
    ) : visibleItemsCount === 0 ? (
      <div className="eme-empty">{emptyLabel}</div>
    ) : (
      <>
        {folders.map((folder) => (
          <div
            key={`folder-${folder.id}`}
            className={`eme-row eme-row--folder ${dragOverId === `folder-${folder.id}` ? 'is-target' : ''}`}
            draggable={!!folder.can_move}
            onDragStart={(event) => startFolderDrag(event, folder)}
            onDragEnd={() => {
              clearSpringFolder();
              setDragPayload(null);
              setDragOverId(null);
            }}
            onDragOver={(event) => handleExplorerDragOver(event, `folder-${folder.id}`, folder.id)}
            onDrop={(event) => onDropExternal(event, folder.id)}
            onContextMenu={(event) => {
              if (canOpenFolderMenu(folder)) openActionMenu('folder', folder, event);
            }}
            onPointerDown={(event) => startLongPressMenu('folder', folder, event)}
            onPointerUp={clearLongPressTimer}
            onPointerCancel={clearLongPressTimer}
            onPointerLeave={clearLongPressTimer}
            onClick={suppressLongPressClick}
            role="listitem"
          >
            <button
              type="button"
              className="eme-name"
              onClick={(event) => {
                if (suppressLongPressClick(event)) return;
                loadTree(folder.id);
              }}
            >
              <i className="fa-solid fa-folder" aria-hidden="true" />
              <span>{folder.name}</span>
            </button>
            <span className={`eme-share ${folder.effective_share_with_artists ? 'is-shared' : 'is-private'}`}>
              {folder.effective_share_with_artists ? '共有' : '非共有'}
            </span>
            <span>{formatDateTimeJST(folder.updated_at || folder.created_at)}</span>
            <span className="eme-actions">
              {canOpenFolderMenu(folder) ? (
                <button
                  type="button"
                  className="eme-row-menu-button"
                  title="メニュー"
                  aria-label={`${folder.name || 'フォルダ'}のメニュー`}
                  onClick={(event) => openActionMenu('folder', folder, event)}
                >
                  <i className="fa-solid fa-ellipsis-vertical" />
                </button>
              ) : null}
            </span>
          </div>
        ))}
        {materials.map((material) => (
          <div
            key={`material-${material.id}`}
            className={`eme-row eme-row--material${selectedMaterialIds.has(material.id) ? ' is-selected' : ''}`}
            draggable={!!(canManage || material.can_delete || material.can_replace)}
            onDragStart={(event) => startMaterialDrag(event, material)}
            onDragEnd={() => {
              clearSpringFolder();
              setDragPayload(null);
              setDragOverId(null);
            }}
            onContextMenu={(event) => {
              if (canOpenMaterialMenu(material)) openActionMenu('material', material, event);
            }}
            onPointerDown={(event) => startLongPressMenu('material', material, event)}
            onPointerUp={clearLongPressTimer}
            onPointerCancel={clearLongPressTimer}
            onPointerLeave={clearLongPressTimer}
            onClick={suppressLongPressClick}
            role="listitem"
          >
            <button
              type="button"
              className="eme-material-check"
              aria-label={selectedMaterialIds.has(material.id) ? '資料の選択を解除' : '資料を選択'}
              aria-pressed={selectedMaterialIds.has(material.id)}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleMaterialSelected(material.id);
              }}
            >
              <i className="fa-solid fa-check" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="eme-name"
              disabled={!canDownload}
              aria-label={`${material.title || material.file_name}${canDownload
                ? (isPdfPreviewable({ fileName: material.file_name, mimeType: material.mime_type || material.content_type }) ? 'をプレビュー' : 'をダウンロード')
                : '（ダウンロード権限なし）'}`}
              onClick={(event) => {
                if (suppressLongPressClick(event)) return;
                if (canDownload) downloadMaterial(material);
              }}
            >
              <i className={`fa-solid ${getFileIcon(material.file_name)}`} aria-hidden="true" />
              <span>
                <strong>{material.title || material.file_name}</strong>
                <small>{material.file_name}{material.file_size ? ` / ${formatBytes(material.file_size)}` : ''}</small>
              </span>
            </button>
            <span className={`eme-share ${material.effective_share_with_artists ? 'is-shared' : 'is-private'}`}>
              {material.folder_id ? (currentFolderShared ? 'フォルダ共有' : 'フォルダ非共有') : (material.effective_share_with_artists ? '共有' : '非共有')}
            </span>
            <span>
              {material.version_label ? `${material.version_label} / ` : ''}
              {formatDateTimeJST(material.updated_at || material.created_at)}
            </span>
            <span className="eme-actions">
              <button
                type="button"
                className="eme-row-menu-button"
                title="メニュー"
                aria-label={`${material.title || material.file_name || '資料'}のメニュー`}
                onClick={(event) => openActionMenu('material', material, event)}
              >
                <i className="fa-solid fa-ellipsis-vertical" />
              </button>
            </span>
          </div>
        ))}
      </>
    )}
  </div>
  );
}
